if (!customElements.get('sender-story-video')) {
  customElements.define('sender-story-video', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => { if (this.isConnected) this.setup(); });
    }

    setup() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.video = this.querySelector('video');
      this.button = this.querySelector('.sr-video__toggle');
      this.poster = this.querySelector('.sr-video__poster');
      if (!this.video || !this.button) return;
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.setAttribute('aria-hidden', 'true');
      if (this.video.readyState >= 2 && !this.failed) this.showVideo();
      this.mobile = matchMedia('(max-width: 915px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.button.hidden = false;
      this.button.addEventListener('click', () => {
        if (this.video.paused) {
          this.userPaused = false;
          this.userRequested = true;
          this.loadSource();
          this.play();
        } else {
          this.userPaused = true;
          this.video.pause();
        }
      }, {signal});
      this.video.addEventListener('loadeddata', () => this.showVideo(), {signal});
      ['playing', 'pause', 'ended'].forEach(event => this.video.addEventListener(event, () => this.updateButton(), {signal}));
      this.video.addEventListener('error', () => this.showFallback(), {signal});
      this.mobile.addEventListener('change', () => { if (this.sourceKey) this.loadSource(); this.sync(); }, {signal});
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) { this.userRequested = false; this.video.pause(); }
        this.sync();
      }, {signal});
      document.addEventListener('visibilitychange', () => this.sync(), {signal});
      window.addEventListener('pagehide', () => this.video.pause(), {signal});
      window.addEventListener('pageshow', () => this.sync(), {signal});
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .1);
          this.sync();
        }, {threshold: [0, .1]});
        this.observer.observe(this.querySelector('.sr-video__frame'));
      } else { this.visible = true; this.sync(); }
      this.updateButton();
    }

    loadSource() {
      const template = this.querySelector(this.mobile.matches ? '[data-video-mobile]' : '[data-video-desktop]');
      const key = template?.innerHTML.trim();
      if (!key || key === this.sourceKey) return;
      this.video.pause();
      this.sourceKey = key;
      this.failed = false;
      this.classList.remove('is-ready', 'is-playing');
      this.poster.removeAttribute('aria-hidden');
      this.video.setAttribute('aria-hidden', 'true');
      this.video.replaceChildren(template.content.cloneNode(true));
      const sources = [...this.video.querySelectorAll('source')];
      let failures = 0;
      sources.forEach(source => source.addEventListener('error', () => {
        failures++;
        if (failures === sources.length && this.sourceKey === key) this.showFallback();
      }, {once: true, signal: this.controller.signal}));
      this.button.hidden = false;
      if (!sources.length) { this.showFallback(); return; }
      this.video.load();
    }

    sync() {
      if (!this.visible || document.hidden || this.userPaused) { this.video.pause(); return; }
      const autoplay = this.dataset.autoplay === 'true' && !this.motion.matches && !navigator.connection?.saveData;
      if ((autoplay || this.userRequested) && !this.failed) { this.loadSource(); this.play(); }
    }

    play() {
      if (this.failed || !this.sourceKey || document.hidden) return;
      this.video.play()?.then(() => {
        if (this.userPaused || !this.visible || document.hidden || (!this.userRequested && (this.motion.matches || navigator.connection?.saveData || this.dataset.autoplay !== 'true'))) this.video.pause();
      }).catch(() => { this.updateButton(); });
    }

    showVideo() {
      this.classList.add('is-ready');
      this.poster.setAttribute('aria-hidden', 'true');
      this.video.removeAttribute('aria-hidden');
    }

    showFallback() {
      this.failed = true;
      this.video.pause();
      this.classList.remove('is-ready', 'is-playing');
      this.poster.removeAttribute('aria-hidden');
      this.video.setAttribute('aria-hidden', 'true');
      this.button.hidden = true;
    }

    updateButton() {
      const playing = !this.video.paused && !this.video.ended;
      this.classList.toggle('is-playing', playing);
      const label = playing ? this.dataset.pauseLabel : this.dataset.playLabel;
      this.button.setAttribute('aria-label', label);
      this.button.querySelector('span').textContent = label;
    }

    disconnectedCallback() {
      this.video?.pause();
      this.controller?.abort();
      this.observer?.disconnect();
    }
  });
}
